import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { api, apiUrl } from "../lib/api";
import { useSettings } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { useImpersonation } from "../lib/impersonation";
import type { Tenant } from "@vereinfach/types";

const TABS = [
  { to: "/", label: "Übersicht" },
  { to: "/members", label: "Mitglieder" },
  { to: "/packages", label: "Beiträge" },
  { to: "/invoices", label: "Rechnungen" },
  { to: "/reports", label: "Finanzen" },
  { to: "/settings", label: "Einstellungen" },
] as const;

export function Shell({
  tenant,
  onLoggedOut,
  impersonating = false,
  children,
}: {
  tenant: Tenant;
  onLoggedOut: () => void;
  impersonating?: boolean;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const { settings } = useSettings();
  const { me } = useAuth();
  const { clear: clearImpersonation } = useImpersonation();
  const navigate = useNavigate();

  async function logout() {
    try {
      await api("/auth/logout", { method: "POST" });
    } finally {
      onLoggedOut();
    }
  }

  function exitImpersonation() {
    clearImpersonation();
    navigate({ to: "/admin" });
  }

  function isActive(to: string): boolean {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(to + "/");
  }

  return (
    <div className="min-h-full">
      {impersonating && (
        <div className="bg-red-600 text-white text-sm">
          <div className="mx-auto max-w-5xl px-4 py-2 flex items-center justify-between gap-3">
            <span>
              Admin-Modus: du verwaltest gerade <span className="font-semibold">{tenant.name}</span>
              .
            </span>
            <button
              onClick={exitImpersonation}
              className="rounded bg-red-700/50 hover:bg-red-700 px-2 py-0.5 text-xs"
            >
              Verlassen
            </button>
          </div>
        </div>
      )}
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" aria-label="Übersicht">
            {settings?.logo_r2_key ? (
              <img
                src={apiUrl(`/tenants/${tenant.id}/settings/logo`)}
                alt={tenant.name}
                className="h-10 w-10 rounded object-contain border border-slate-200 bg-white"
              />
            ) : (
              <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold">
                {tenant.name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-lg font-semibold tracking-tight group-hover:text-slate-700">
                {tenant.name}
              </h1>
              <p className="text-xs text-slate-500">Vereinfach</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            {me?.is_admin && (
              <Link to="/admin" className="text-sm text-slate-600 hover:text-slate-900">
                Admin
              </Link>
            )}
            <button onClick={logout} className="text-sm text-slate-600 hover:text-slate-900">
              Abmelden
            </button>
          </div>
        </div>
        <nav className="mx-auto max-w-5xl px-4 flex gap-6 overflow-x-auto overflow-y-hidden no-scrollbar">
          {TABS.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className={
                "py-3 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap " +
                (isActive(t.to)
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-900")
              }
            >
              {t.label}
            </Link>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8">{children}</main>
    </div>
  );
}
